'use client';

import type { Faro } from '@grafana/faro-web-sdk';

let faroInstance: Faro | undefined;
let initPromise: Promise<Faro | undefined> | undefined;

const appName = (): string => process.env.NEXT_PUBLIC_FARO_APP_NAME ?? 'meissa';

/**
 * The identifier that ties a Faro session to the source-map bundle uploaded
 * by scripts/upload-faro-sourcemaps.mjs. Same fallback chain used as `app.version`
 * below and read verbatim (same env vars, same build) by the upload script,
 * so the two always agree without any bundler-specific wiring (works
 * identically under Turbopack and webpack).
 */
const faroBundleId = (): string | undefined =>
  process.env.NEXT_PUBLIC_FARO_APP_VERSION ??
  process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;

const faroEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const url = process.env.NEXT_PUBLIC_FARO_URL;
  return typeof url === 'string' && url.length > 0;
};

/**
 * Initializes Grafana Faro (Frontend Observability) once. No-op on the server
 * and whenever NEXT_PUBLIC_FARO_URL is not configured — there's no separate
 * enabled flag, since a configured collector URL already fully determines
 * whether Faro should run. The SDK is imported lazily so it never ships in
 * the client bundle unless enabled. Idempotent, safe to call more than once
 * (HMR, concurrent callers).
 */
export const initFaro = (): Promise<Faro | undefined> => {
  if (faroInstance) {
    return Promise.resolve(faroInstance);
  }
  if (initPromise) {
    return initPromise;
  }
  if (!faroEnabled()) {
    return Promise.resolve(undefined);
  }

  initPromise = (async () => {
    const [
      { getWebInstrumentations, initializeFaro },
      { TracingInstrumentation },
    ] = await Promise.all([
      import('@grafana/faro-web-sdk'),
      import('@grafana/faro-web-tracing'),
    ]);

    const name = appName();
    const bundleId = faroBundleId();
    if (bundleId) {
      // Faro auto-detects the uploaded source-map bundle by reading this
      // global — must be set before initializeFaro() runs.
      (window as unknown as Record<string, string>)[`__faroBundleId_${name}`] =
        bundleId;
    }

    faroInstance = initializeFaro({
      url: process.env.NEXT_PUBLIC_FARO_URL as string,
      app: {
        name,
        version: bundleId,
        environment:
          process.env.NEXT_PUBLIC_FARO_APP_ENVIRONMENT ?? 'production',
      },
      instrumentations: [
        ...getWebInstrumentations({ captureConsole: false }),
        // Attaches W3C traceparent/tracestate to outbound fetch/XHR calls so
        // RUM sessions join the backend traces from src/instrumentation.node.ts.
        new TracingInstrumentation(),
      ],
    });

    return faroInstance;
  })();

  return initPromise;
};

/**
 * Records a view change (e.g. an App Router navigation) against the current
 * Faro session. No-op until Faro has finished initializing. This is the
 * Next.js App Router substitute for Faro's React Router integration
 * (ReactIntegration/createReactRouterV7DataOptions), which only applies to
 * react-router-dom.
 */
export const trackView = (name: string): void => {
  faroInstance?.api.setView({ name });
};

export { faroEnabled };
